import { Box } from "@rebass/grid";
import Spacer, {
  ESpacerWeight,
} from "@rentiohq/shared-frontend/dist/components/components/Spacer";
import { useQueryParams } from "@rentiohq/shared-frontend/dist/hooks/useQueryParams";
import { getName } from "@rentiohq/shared-frontend/dist/redux/contact/contact.utils";
import * as applicationHooks from "@rentiohq/shared-frontend/dist/reduxV2/applications/application.hooks";
import { formatAddress } from "@rentiohq/shared-frontend/dist/utils/address.utils";
import { getLocalizedText } from "@rentiohq/shared-frontend/dist/utils/i18n/i18n.utils";
import { formatCurrency } from "@rentiohq/shared-frontend/dist/utils/number.utils";
import { formatPhone } from "@rentiohq/shared-frontend/dist/utils/phone/phone.utils";
import {
  Activities,
  Button,
  Card,
  DisplayText,
  Error,
  ESpacings,
  Grid,
  Icon,
  Loading,
  Page,
  ResourceList,
  ResourceListItem,
  Stack,
  TextStyle,
} from "@rentiohq/web-shared/dist/components";
import { IAction, TActions } from "@rentiohq/web-shared/dist/types";
import utils from "@rentiohq/web-shared/dist/utils";
import { InlineNotes } from "components/InlineNotes";
import flatten from "lodash/flatten";
import React from "react";
import { StringParam } from "serialize-query-params";
import { ts } from "services";
import { ApplicationDetailHistory } from "../ApplicationDetailHistory";
import * as applicationUtils from "./application.utils";
import {
  EPdfStatus,
  getContactActions,
  getViewActions,
} from "./applicationActions.utils";
import { EApplicationDetailTabs } from "./ApplicationDetail.types";

interface IProps {
  applicationId: string;
  onDrawerClose?: () => void;
}

const renderCardHeader = (heading: string) => (
  <DisplayText size="extraSmall" space="tight">
    {heading}
  </DisplayText>
);

const renderRow = (item: {
  title: string;
  value?: string | number;
  action?: IAction;
}) => {
  let formattedValue = item.value;
  if (
    !item.value ||
    (typeof item.value === "string" && item.value.length === 0)
  ) {
    formattedValue = getLocalizedText("tenant_profile.pdf.item.empty");
  }

  return (
    <ResourceListItem
      item={item}
      primaryActions={item.action ? [item.action] : undefined}
    >
      <TextStyle variation="subdued" element="div">
        {item.title}
      </TextStyle>
      <div style={{ whiteSpace: "pre-line" }}>{formattedValue}</div>
    </ResourceListItem>
  );
};

export const ApplicationDetail = (props: IProps) => {
  const { applicationId } = props;

  const [, setQueryParamValue] = useQueryParams({
    applicationId: StringParam,
  });

  const [tab, setTab] = React.useState<EApplicationDetailTabs>(
    EApplicationDetailTabs.Info,
  );
  const [pdfStatus, setPdfStatus] = React.useState(EPdfStatus.Checking);

  // Redux
  const {
    detail: application,
    isFetching,
    fetchError,
  } = applicationHooks.useDetail({
    shouldRefetch: false,
    id: applicationId,
  });

  React.useEffect(() => {
    if (application) {
      checkAvailability();
    }
  }, [application]);

  // Render
  if (!application) {
    if (isFetching) {
      return <Loading />;
    }

    if (fetchError) {
      return <Error errors={[fetchError]} />;
    }

    return null;
  }

  const {
    contacts,
    dateOfOccupancy,
    reasonForRent,
    reasonForRentOther,
    currentResidenceAddress,
    financialRemarks,
    propertyId,
    property,
    pdfUrl,
  } = application;
  const contact = contacts[0];

  const subtitle = applicationUtils.getSetInfo(application);

  const checkAvailability = async () => {
    if (!pdfUrl) {
      setPdfStatus(EPdfStatus.NotAvailable);
    }

    try {
      const response = await fetch(pdfUrl as URL | RequestInfo, {
        method: "GET",
      });
      if (response.status === 200) {
        setPdfStatus(EPdfStatus.Generated);
      } else {
        setPdfStatus(EPdfStatus.Generating);
      }
    } catch (error) {
      setPdfStatus(EPdfStatus.NotAvailable);
    }
  };

  const metadata = (
    <div>
      <Spacer weight={ESpacerWeight.W08} />
      {property && propertyId && (
        <Stack.Item>
          <Grid spacing="extraTight" alignItems="center">
            <Grid.Item>
              <Icon
                source={utils.properties.getIcon(
                  property.typeId ||
                    // @ts-ignore
                    property.type ||
                    0,
                )}
                size="small"
              />
            </Grid.Item>
            <Grid.Item>
              <Button
                appearance="link"
                url={`/properties/${propertyId}/applications`}
              >
                {formatAddress(property)}
              </Button>
            </Grid.Item>
          </Grid>
        </Stack.Item>
      )}
    </div>
  );

  const handleInfoTabClick = () => {
    setTab(EApplicationDetailTabs.Info);
  };

  const handleHistoryTabClick = () => {
    setTab(EApplicationDetailTabs.History);
  };

  const handleActivityMoreClick = () => {
    setTab?.(EApplicationDetailTabs.History);
  };

  const dropdownActions: TActions = [
    getViewActions({
      item: application,
      pdfStatus,
      setQueryParamValue,
      isDetailView: true,
    }),
  ];

  let reasonForRentValue = "";
  if (reasonForRentOther) {
    reasonForRentValue = reasonForRentOther;
  } else if (reasonForRent) {
    reasonForRentValue = getLocalizedText(reasonForRent);
  }

  const sections: {
    title: string;
    subtitle: string;
    actions?: IAction[];
    items: {
      title: string;
      value?: string | number;
      action?: IAction;
    }[];
  }[] = [
    {
      title: getLocalizedText("tenant_profile.pdf.section.general"),
      subtitle: "",
      actions: getContactActions({
        item: application,
      }),
      items: [
        {
          title: getLocalizedText("system.full_name"),
          value: getName(contact),
        },
        { title: getLocalizedText("system.email"), value: contact.email },
        {
          title: getLocalizedText("system.mobile_number"),
          value: contact.phone ? formatPhone(contact.phone) : "-",
        },
        {
          title: getLocalizedText("system.locale"),
          value: contact.language
            ? getLocalizedText(`system.locale.${contact.language}`)
            : "-",
        },
      ],
    },
    {
      title: getLocalizedText("tenant_profile.pdf.section.residents"),
      subtitle: "",
      items: application.residents.map(({ id, value }) => ({
        title: getLocalizedText(id),
        value:
          value === 0 ? getLocalizedText(`${id}.empty`.toLowerCase()) : value,
      })),
    },
    {
      title: getLocalizedText("tenant_profile.pdf.section.rent_info"),
      subtitle: "",
      items: [
        {
          title: getLocalizedText("tenant_profile.pdf.item.date_of_occupancy"),
          value: dateOfOccupancy ? utils.date.format(dateOfOccupancy) : "-",
        },
        {
          title: getLocalizedText("tenant_profile.pdf.item.reason_for_rent"),
          value: reasonForRentValue,
        },
        {
          title: getLocalizedText("tenant_profile.pdf.item.current_residence"),
          value: formatAddress(currentResidenceAddress),
        },
      ],
    },
    // All tenants
    ...contacts.map((contact, index: number) => ({
      title: `${getName(contact)}`,
      subtitle: "",
      actions: getContactActions({
        item: application,
        contact,
      }),
      items: [
        {
          title: contact.isGuarantor
            ? getLocalizedText("system.guarantor")
            : getLocalizedText("system.tenant"),
          value: getName(contact),
        },
        // TODO: @dries: add address to contact type
        // @ts-ignore
        {
          title: getLocalizedText("system.address"),
          // @ts-ignore
          value: contact.address ? formatAddress(contact.address) : "",
        },
        { title: getLocalizedText("system.email"), value: contact.email },
        {
          title: getLocalizedText("system.mobile_number"),
          value: contact.phone ? formatPhone(contact.phone) : "-",
        },
        ...(contact.income || []).map(income => ({
          title: getLocalizedText(income.id),
          value: formatCurrency(income.value || 0, true),
        })),
        ...(index === 0
          ? [
              {
                title: getLocalizedText("system.comments"),
                value: financialRemarks,
              },
            ]
          : []),
      ],
    })),
    {
      title: getLocalizedText("system.comments_other"),
      subtitle: "",
      items: [{ title: getLocalizedText("system.comments"), value: "" }],
    },
    {
      title: getLocalizedText("system.documents"),
      subtitle: "",
      items: [
        // ID's
        ...flatten(
          contacts.map(contact =>
            (contact.documents || []).map(document => ({
              title: getLocalizedText("intake.contract.id.title"),
              value: getName(contact),
              action: {
                id: "open",
                content: getLocalizedText("document.open"),
                onClick: () => {
                  window.open(document, "_blank");
                },
              },
            })),
          ),
        ),
        // Income
        ...flatten(
          flatten(
            contacts.map(contact =>
              (contact.income || []).map(income =>
                (income.documents || []).map(document => ({
                  title: `${getLocalizedText(income.id)} (${formatCurrency(
                    income.value || 0,
                  )})`,
                  value: getName(contact),
                  action: {
                    id: "open",
                    content: getLocalizedText("document.open"),
                    onClick: () => {
                      window.open(document, "_blank");
                    },
                  },
                })),
              ),
            ),
          ),
        ),
      ],
    },
  ];

  let tabs = [
    {
      name: "dashboard",
      permission: "propertyDashboard:visit",
      content: ts.paymentRequestDetailTabsInfo(),
      onClick: handleInfoTabClick,
      isActive: tab === EApplicationDetailTabs.Info,
    },
    {
      name: "payments",
      permission: "propertyPayments:visit",
      content: ts.paymentRequestDetailTabsHistory(),
      onClick: handleHistoryTabClick,
      isActive: tab === EApplicationDetailTabs.History,
    },
  ];

  const renderInfo = () => (
    <React.Fragment>
      <InlineNotes
        customPath={`/applications/${application.id}/notes`}
        cardProps={{
          heading: renderCardHeader(getLocalizedText("system.model.note")),
        }}
      />

      {sections.map(({ title, items, actions }) => (
        <Card
          key={title}
          heading={renderCardHeader(title)}
          subHeading={undefined}
          actions={actions}
          space={ESpacings.loose}
        >
          <ResourceList items={items} renderItem={renderRow} />
        </Card>
      ))}

      <Card
        heading={renderCardHeader(ts.system("history"))}
        space={ESpacings.base}
      >
        <Box mb={ESpacings.base}>
          <Activities
            identifier={`application-${application.id}-compact`}
            filter={{
              where: {
                applicationId: application.id,
              },
            }}
            limit={3}
            hasFetchMore={false}
            isCompact={true}
            onMoreClick={handleActivityMoreClick}
          />
        </Box>
      </Card>
    </React.Fragment>
  );

  const renderHistory = () => (
    <ApplicationDetailHistory application={application} setTab={setTab} />
  );

  return (
    <Page
      title={getName(contact)}
      subtitle={subtitle}
      metadata={metadata}
      tabs={tabs}
      dropdown={dropdownActions}
    >
      {tab === EApplicationDetailTabs.Info && renderInfo()}
      {tab === EApplicationDetailTabs.History && renderHistory()}
    </Page>
  );
};
